// Inventory service
// - Responsible for CRUD operations against the inventory_items table
// - Encapsulates household scoping and basic error handling

import { type InventoryItem, supabase } from "../lib/supabase";

export type { InventoryItem };

export const inventoryService = {
  // Fetch items for a given location within the current user's household
  // Returns an empty array if no household is found
  async getItems(
    location: "fridge" | "freezer" | "pantry" | "other",
    householdId: string,
  ): Promise<InventoryItem[]> {
    if (!location) {
      return [];
    }

    const { data, error } = await supabase
      .from("inventory_items")
      .select(
        "id, user_id, household_id, item_name, quantity, unit, location, category, created_at, updated_at",
      )
      .eq("location", location)
      .eq("household_id", householdId)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Fetch all inventory items for the current user's household
  // Returns an empty array if no household is found
  async getAllItems(householdId: string): Promise<InventoryItem[]> {
    const { data, error } = await supabase
      .from("inventory_items")
      .select(
        "id, user_id, household_id, item_name, quantity, unit, location, category, created_at, updated_at",
      )
      .eq("household_id", householdId)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Create a new inventory item scoped to the current user's household
  // Throws when household cannot be resolved
  async addItem(
    item_name: string,
    quantity: number | null,
    unit: string | null,
    location: "fridge" | "freezer" | "pantry" | "other",
    category: string | null = "",
    householdId: string,
    userId: string,
  ): Promise<InventoryItem> {
    const { data, error } = await supabase
      .from("inventory_items")
      .insert([
        {
          user_id: userId,
          household_id: householdId,
          item_name,
          quantity,
          unit,
          location,
          category,
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Update an existing inventory item by id
  // Returns the updated item or throws on error
  async updateItem(
    id: string,
    updates: Partial<InventoryItem>,
  ): Promise<InventoryItem> {
    const { data, error } = await supabase
      .from("inventory_items")
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Delete an inventory item by id
  // No-op on error, logs are handled by supabase client in callers when needed
  async deleteItem(id: string): Promise<void> {
    const { error } = await supabase
      .from("inventory_items")
      .delete()
      .eq("id", id);

    if (error) throw error;
  },
};
